import {
  type PriceLookupKeyEnum,
  TierLookupKeyEnum,
} from "~/api/generated/model";
import { PRICE_LOOKUP_KEYS } from "~/routes/(restricted)/checkout/-utils/keys";
import { AppPermission } from "~/utils/permissions";

type TierData = {
  pending?: true;
  targetAppPermission: AppPermission | null;
  priceLookupKey: PriceLookupKeyEnum | null;
  name: string;
  description: string;
  inherits?: string;
  allSubTiers?: ProductTier[];
  includes: TierIncludes[];
  upgrade: {
    heading: string;
    description: string;
  } | null;
};

type TierIncludes = {
  section: string;
  items: { name: string; pending?: boolean }[];
};

export type ProductTier = "Gratis" | TierLookupKeyEnum;

export const ALL_TIERS: ProductTier[] = [
  "Gratis",
  "Basic",
  "Premium",
  "Analyse",
];
export const CONSUMER_TIERS: TierLookupKeyEnum[] = [
  "Basic",
  "Premium",
  "Analyse",
];

export const isSelectableProductTier = (
  tier: ProductTier,
): tier is TierLookupKeyEnum => tier !== "Gratis";

export const getAllSubTirs = (tier: ProductTier) => {
  const index = ALL_TIERS.indexOf(tier);
  return ALL_TIERS.slice(0, index);
};

export const TIER_DATA = new Map<ProductTier, TierData>([
  [
    "Gratis",
    {
      targetAppPermission: null,
      priceLookupKey: null,
      name: "Gratis",
      description: "Kom i gang uden at betale",
      includes: [
        {
          section: "Ejendomstjek",
          items: [{ name: "Overblik" }],
        },
        {
          section: "Portefølje",
          items: [
            {
              name: "Virksomheder",
            },
            {
              name: "Ejendomsportefølje",
              pending: true,
            },
          ],
        },
      ],
      upgrade: null,
    },
  ],
  [
    TierLookupKeyEnum.Basic,
    {
      targetAppPermission: AppPermission.propertyCheck,
      priceLookupKey: PRICE_LOOKUP_KEYS[TierLookupKeyEnum.Basic],
      name: "Basic",
      description: "Hele Ejendomstjek",
      inherits: "Alt fra Gratis",
      allSubTiers: ["Gratis"],
      includes: [
        {
          section: "Ejendomstjek",
          items: [
            {
              name: "Huslejeanalyse",
            },
            {
              name: "Udlejning",
            },
            {
              name: "Miljø og restriktioner",
            },
            {
              name: "Salgspriser",
            },
          ],
        },
      ],
      upgrade: {
        heading: "Abonnér på Basic for at få adgang til Ejendomstjek",
        description:
          "Et Basic abonnement giver dig fuld adgang til Ejendomstjek.",
      },
    },
  ],
  [
    TierLookupKeyEnum.Premium,
    {
      targetAppPermission: AppPermission.planningProposal,
      priceLookupKey: PRICE_LOOKUP_KEYS[TierLookupKeyEnum.Premium],
      name: "Premium",
      description: "Find dit næste projekt med ByggeRadar",
      inherits: "Alt fra Basic",
      allSubTiers: ["Gratis", "Basic"],
      includes: [
        {
          section: "ByggeRadar",
          items: [
            {
              name: "Screening af kommunale referater",
            },
            {
              name: "Agenter & e-mailnotifikationer",
            },
          ],
        },
      ],
      upgrade: {
        heading: "Abonnér på Premium for at få adgang til ByggeRadar",
        description:
          "Et Premium abonnement giver dig fuld adgang til både ByggeRadar og Ejendomstjek.",
      },
    },
  ],
  [
    TierLookupKeyEnum.Analyse,
    {
      targetAppPermission: AppPermission.reports,
      priceLookupKey: PRICE_LOOKUP_KEYS[TierLookupKeyEnum.Analyse],
      name: "Analyse",
      description: "Fuldt overblik med Power BI-rapporter",
      inherits: "Alt fra Premium",
      allSubTiers: ["Gratis", "Basic", "Premium"],
      includes: [
        {
          section: "Analyse",
          items: [
            {
              name: "Power BI-rapporter",
            },
            {
              name: "Eksport af data",
            },
          ],
        },
      ],
      upgrade: {
        heading: "Abonnér på Analyse for at få adgang til Rapporter",
        description:
          "Et Analyse abonnement giver dig fuld adgang til både Rapporter, ByggeRadar og Ejendomstjek.",
      },
    },
  ],
]);
